import { skipToken } from '@reduxjs/toolkit/query';
import { ShareDraftDialog } from 'components/drafts/ShareDraftDialog';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { createContext, useState } from 'react';
import { useDeleteDraftMutation, useGetDraftQuery, useGetDraftsQuery } from 'store/api/drafts';
import type { DraftKey } from 'types/drafts';
import { FeatureFlag } from 'types/features.ts';
import { successAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';

type Options = {
    /** The draft; `null` where the record is no draft, so nothing is read. */
    draft: DraftKey | null;
    /** The record's create route: Edit opens the draft there, with `?draft=<id>`. A list row's menu has no Edit. */
    createRoute?: string;
    /** Runs once the draft is deleted, as a record page then leaves it. */
    onDeleted?: () => void;
    /** A list row's: read from the drafts list, which every row of the list shares, rather than one read per row. */
    listed?: boolean;
};

/**
 * What the reader of a draft may do with it, by the right it gives them: Edit for Can edit and Owner, Share and
 * Delete draft for Owner. Share also needs the drafts flag; Edit and Delete do not, so a draft saved before the flag
 * went off is still completed or deleted.
 */
export function useDraftActions({ draft: key, createRoute, onDeleted, listed = false }: Options) {
    const isDraftsEnabled = useIsFeatureEnabled(FeatureFlag.SAVE_AS_DRAFT);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSharing, setIsSharing] = useState(false);
    // Let go of while the draft is deleted, so its deletion does not read it again into a 404.
    const { data: read } = useGetDraftQuery(key && !listed ? key : skipToken, { skip: isDeleting });
    const { data: inList } = useGetDraftsQuery(undefined, {
        skip: !key || !listed,
        selectFromResult: ({ data }) => ({
            data: data?.find((listedDraft) => listedDraft.type === key?.type && listedDraft.id === key.id),
        }),
    });
    const draft = listed ? inList : read;
    const [deleteDraft] = useDeleteDraftMutation();
    const { openModal, closeModal } = useModal();
    const navigateWithReturn = useNavigateWithReturnState();
    const right = draft?.right;

    const askDelete = () => {
        if (!key) return;
        openModal(
            <ConfirmationModal
                titleId="draft.delete-title"
                confirmId="delete-draft"
                onCancel={closeModal}
                onConfirm={() => {
                    closeModal();
                    setIsDeleting(true);
                    void deleteDraft(key)
                        .unwrap()
                        .then(() => {
                            successAlert(t('create-dialog.saved.deleted'));
                            onDeleted?.();
                        })
                        // The API's error handler toasts the failure.
                        .catch(() => setIsDeleting(false));
                }}
                content={<p className="text-body1">{t('draft.delete-body')}</p>}
            />
        );
    };

    return {
        draft,
        canEdit: right === 'EDIT' || right === 'OWNER',
        canShare: right === 'OWNER' && isDraftsEnabled,
        canDelete: right === 'OWNER',
        edit: () => key && createRoute && navigateWithReturn(`${createRoute}?draft=${key.id}`),
        /** Edit, with the focus on the first required field the draft misses. */
        fill: () => key && createRoute && navigateWithReturn(`${createRoute}?draft=${key.id}&fill`),
        share: () => setIsSharing(true),
        askDelete,
        shareDialog:
            key && isSharing ? <ShareDraftDialog draft={key} open onClose={() => setIsSharing(false)} /> : null,
    };
}

export type DraftActions = ReturnType<typeof useDraftActions>;

/** The draft a record page shows, for the views of its tabs; `null` on a record that is no draft. */
export const DraftActionsContext = createContext<DraftActions | null>(null);
